import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppState } from '../contexts/AppStateContext';
import { useNotifications } from '../contexts/NotificationContext';
import { networkUtils } from '../lib/queryClient';

// Hook for offline support and error recovery
export function useOfflineSupport() {
  const queryClient = useQueryClient();
  const { state, dispatch } = useAppState();
  const { addNotification, addSystemAlert, removeSystemAlert } = useNotifications();

  // Setup network listeners
  useEffect(() => {
    const cleanup = networkUtils.setupNetworkListeners();
    return cleanup;
  }, []);

  // Handle online/offline status changes
  useEffect(() => {
    const handleOnline = () => {
      dispatch({ type: 'SET_ONLINE_STATUS', payload: true });
      
      // Remove offline alert
      removeSystemAlert('offline-alert');
      
      // Show reconnection notification
      addNotification({
        title: 'Connection Restored',
        type: 'success',
        message: 'Connection restored. Syncing data...',
        duration: 3000,
      });
      
      // Resume paused mutations and refetch stale queries
      queryClient.resumePausedMutations().then(() => {
        queryClient.invalidateQueries();
      });
      
      dispatch({ type: 'SET_LAST_SYNC_TIME', payload: new Date() });
    };

    const handleOffline = () => {
      dispatch({ type: 'SET_ONLINE_STATUS', payload: false });
      
      // Show offline alert
      addSystemAlert({
        type: 'warning',
        title: 'You are offline',
        message: 'Some features may be limited. Changes will be synced when connection is restored.',
        dismissible: false,
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [dispatch, addNotification, addSystemAlert, removeSystemAlert, queryClient]);

  // Retry failed mutations when coming back online
  const retryFailedMutations = useCallback(async () => {
    if (!state.isOnline) return;

    try {
      await queryClient.resumePausedMutations();
      addNotification({
        title: 'Sync Complete',
        type: 'success',
        message: 'Pending changes have been synced',
        duration: 3000,
      });
    } catch (error) {
      addNotification({
        title: 'Sync Failed',
        type: 'error',
        message: 'Some changes could not be synced. Please try again.',
        duration: 5000,
      });
    }
  }, [state.isOnline, queryClient, addNotification]);

  // Force sync all data
  const forceSyncAll = useCallback(async () => {
    if (!state.isOnline) {
      addNotification({
        title: 'Offline',
        type: 'warning',
        message: 'Cannot sync while offline',
        duration: 3000,
      });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Invalidate all queries to force refetch
      await queryClient.invalidateQueries();
      
      dispatch({ type: 'SET_LAST_SYNC_TIME', payload: new Date() });
      
      addNotification({
        title: 'Sync Complete',
        type: 'success',
        message: 'All data synced successfully',
        duration: 3000,
      });
    } catch (error) {
      addNotification({
        title: 'Sync Failed',
        type: 'error',
        message: 'Sync failed. Please try again.',
        duration: 5000,
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.isOnline, queryClient, dispatch, addNotification]);

  // Clear cache and reload
  const clearCacheAndReload = useCallback(() => {
    queryClient.clear();
    dispatch({ type: 'SET_CACHE_CLEARED', payload: new Date() });
    
    addNotification({
      title: 'Cache Cleared',
      type: 'info',
      message: 'Cache cleared. Reloading data...',
      duration: 3000,
    });
    
    // Reload the page to start fresh
    window.location.reload();
  }, [queryClient, dispatch, addNotification]);

  // Get offline queue status
  const getOfflineQueueStatus = useCallback(() => {
    const mutationCache = queryClient.getMutationCache();
    const pendingMutations = mutationCache.getAll().filter(
      mutation => mutation.state.status === 'pending'
    );
    
    return {
      hasPendingMutations: pendingMutations.length > 0,
      pendingCount: pendingMutations.length,
      mutations: pendingMutations.map(mutation => ({
        id: mutation.mutationId,
        status: mutation.state.status,
        variables: mutation.state.variables,
      })),
    };
  }, [queryClient]);

  // Auto-retry mechanism for failed queries
  useEffect(() => {
    if (!state.isOnline) return;

    const interval = setInterval(() => {
      const queryCache = queryClient.getQueryCache();
      const failedQueries = queryCache.getAll().filter(
        query => query.state.status === 'error' && query.getObserversCount() > 0
      );

      if (failedQueries.length > 0) {
        failedQueries.forEach(query => {
          query.fetch();
        });
      }
    }, 30000); // Retry every 30 seconds

    return () => clearInterval(interval);
  }, [state.isOnline, queryClient]);

  // Periodic sync when online
  useEffect(() => {
    if (!state.isOnline) return;

    const interval = setInterval(() => {
      // Only sync if there are active observers (user is actively using the app)
      const queryCache = queryClient.getQueryCache();
      const activeQueries = queryCache.getAll().filter(
        query => query.getObserversCount() > 0
      );

      if (activeQueries.length > 0) {
        queryClient.invalidateQueries({ stale: true });
      }
    }, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(interval);
  }, [state.isOnline, queryClient]);

  return {
    isOnline: state.isOnline,
    lastSyncTime: state.lastSyncTime,
    retryFailedMutations,
    forceSyncAll,
    clearCacheAndReload,
    getOfflineQueueStatus,
  };
}

// Hook for connection quality monitoring
export function useConnectionQuality() {
  const { addNotification } = useNotifications();

  useEffect(() => {
    // Monitor connection quality using navigator.connection if available
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      
      const handleConnectionChange = () => {
        const effectiveType = connection.effectiveType;
        
        if (effectiveType === 'slow-2g' || effectiveType === '2g') {
          addNotification({
            title: 'Slow Connection',
            type: 'warning',
            message: 'Slow connection detected. Some features may be limited.',
            duration: 5000,
          });
        }
      };

      connection.addEventListener('change', handleConnectionChange);
      
      return () => {
        connection.removeEventListener('change', handleConnectionChange);
      };
    }
  }, [addNotification]);

  // Simple ping test for connection quality
  const testConnectionSpeed = useCallback(async () => {
    const startTime = Date.now();
    
    try {
      await fetch('/api/ping', { 
        method: 'HEAD',
        cache: 'no-cache',
      });
      
      const endTime = Date.now();
      const latency = endTime - startTime;
      
      return {
        latency,
        quality: latency < 100 ? 'excellent' : 
                latency < 300 ? 'good' : 
                latency < 1000 ? 'fair' : 'poor'
      };
    } catch (error) {
      return {
        latency: -1,
        quality: 'offline' as const,
      };
    }
  }, []);

  return {
    testConnectionSpeed,
  };
}

// Hook for background sync management
export function useBackgroundSync() {
  const queryClient = useQueryClient();
  const { state } = useAppState();

  // Register service worker for background sync (if supported)
  useEffect(() => {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      navigator.serviceWorker.ready.then(registration => {
        // Register background sync
        // Check if sync is available (not supported in all browsers)
        if ('sync' in registration) {
          return (registration as any).sync.register('background-sync');
        }
        return Promise.resolve();
      }).catch(error => {
        console.warn('Background sync registration failed:', error);
      });
    }
  }, []);

  // Listen for background sync events
  useEffect(() => {
    const handleBackgroundSync = () => {
      if (state.isOnline) {
        queryClient.resumePausedMutations();
        queryClient.invalidateQueries({ stale: true });
      }
    };

    navigator.serviceWorker?.addEventListener('message', (event) => {
      if (event.data.type === 'BACKGROUND_SYNC') {
        handleBackgroundSync();
      }
    });
  }, [queryClient, state.isOnline]);

  return {
    isBackgroundSyncSupported: 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype,
  };
}